// Tiny hash router: "#/sestanek/<id>" etc. Hash routing keeps the server and service worker simple.
import { useEffect, useState } from 'react';

export type Route =
  | { name: 'home' }
  | { name: 'meeting'; id: string }
  | { name: 'inspection'; id: string }
  | { name: 'notes'; id?: string }
  | { name: 'settings' }
  | { name: 'login' }
  /** Home-screen shortcut ("#/hitro/opomnik"): a page with one button that starts a new note / meeting / inspection. */
  | { name: 'quick'; mode: QuickMode }
  | { name: 'notFound' };

export type QuickMode = 'note' | 'meeting' | 'inspection';

const QUICK_MODES: Record<string, QuickMode> = { opomnik: 'note', sestanek: 'meeting', nadzor: 'inspection' };
const QUICK_SLUGS: Record<QuickMode, string> = { note: 'opomnik', meeting: 'sestanek', inspection: 'nadzor' };

export function parseRoute(hash: string): Route {
  let parts: string[];
  try {
    parts = hash.replace(/^#\/?/, '').split('/').filter(Boolean).map(decodeURIComponent);
  } catch {
    // Malformed escape (e.g. a link cut off by a mail client): show "not found", never crash.
    return { name: 'notFound' };
  }
  const [first, second] = parts;
  switch (first) {
    case undefined:
      return { name: 'home' };
    case 'sestanek':
      return second ? { name: 'meeting', id: second } : { name: 'notFound' };
    case 'nadzor':
      return second ? { name: 'inspection', id: second } : { name: 'notFound' };
    case 'opomniki':
      return second ? { name: 'notes', id: second } : { name: 'notes' };
    case 'nastavitve':
      return { name: 'settings' };
    case 'prijava':
      return { name: 'login' };
    case 'hitro': {
      const mode = second ? QUICK_MODES[second] : undefined;
      return mode ? { name: 'quick', mode } : { name: 'notFound' };
    }
    default:
      return { name: 'notFound' };
  }
}

export function routeToHash(route: Route): string {
  switch (route.name) {
    case 'home':
      return '#/';
    case 'meeting':
      return `#/sestanek/${encodeURIComponent(route.id)}`;
    case 'inspection':
      return `#/nadzor/${encodeURIComponent(route.id)}`;
    case 'notes':
      return route.id ? `#/opomniki/${encodeURIComponent(route.id)}` : '#/opomniki';
    case 'settings':
      return '#/nastavitve';
    case 'login':
      return '#/prijava';
    case 'quick':
      return `#/hitro/${QUICK_SLUGS[route.mode]}`;
    case 'notFound':
      return '#/';
  }
}

export function navigate(route: Route, opts: { replace?: boolean } = {}): void {
  const hash = routeToHash(route);
  if (opts.replace) {
    history.replaceState(null, '', hash);
    window.dispatchEvent(new HashChangeEvent('hashchange'));
  } else {
    window.location.hash = hash;
  }
}

export function useRoute(): Route {
  const [route, setRoute] = useState<Route>(() => parseRoute(window.location.hash));
  useEffect(() => {
    const onChange = () => setRoute(parseRoute(window.location.hash));
    window.addEventListener('hashchange', onChange);
    return () => window.removeEventListener('hashchange', onChange);
  }, []);
  return route;
}
